/**
 * FACE in the right-hand bar — the switch between financial sections, available on every page.
 *
 * It lives in the layout rather than on a page so that it is always to hand, and the selection
 * survives navigation because the layout does. Nothing consumes the selection yet: the pages it
 * will switch (the financials, analysis and evidence behind each segment) do not exist, so there is
 * no panel for the tabs to control.
 *
 * **It reports nothing yet**, because three things are undecided and all of them are the financial
 * team's:
 *
 *  1. **Which finsets belong to which segment.** Some of it falls out of the codes — `-CFSTT` is
 *     Cash, `-INCST` is Earnings — but `-BALST` mixes Assets and Financing in one suffix, and the
 *     eleven `ANL-` ratios carry no statement at all. It is a judgement over ~26 codes, so it wants
 *     a `face_segment` column on the FAMS / CONADD / ANL registries rather than a rule here.
 *  2. **What Strong / Mixed / Weak asserts.** "The company is strong in this area" is an analytical
 *     claim nothing currently produces. "Our evidence here is strong" is computable today from
 *     coverage — `families_declared` against `families_built`, `analyses_total` against
 *     `analyses_computed`. The second is free and arguably the more useful thing for a navigation
 *     control to say: a segment reading Weak tells you there is little behind the button.
 *  3. **What the centre score is, and what each segment's trend line plots.** The first mock-up
 *     showed 8.2 / 10 and a rising line in every segment; no stage of the pipeline computes either.
 *
 * Until those are settled the switch gets no readings, so every segment says "Not rated" and the
 * centre carries no meter. Showing invented labels would be worse than showing none — the page
 * would assert something the system has not concluded, which is the failure every other part of
 * this pipeline is built to avoid.
 */

import { useId, useState } from 'react';

import { FaceSwitch, type FaceKey } from 'components/face/FaceSwitch';

export default function FacePanel() {
  const id = useId();
  const [segment, setSegment] = useState<FaceKey>('cash');

  return <FaceSwitch id={id} value={segment} onChange={setSegment} />;
}
